import { LocalCache, StorageKey } from "../../../../../cache/LocalCache"
import type { ResourceFilterElementValue, ResourceFiltersSelectedItem } from "../../../createOrderTypes"

export const SERVICE_CENTER_FILTER_IDENTIFIER = "serviceCenterIds"

// Keyed by effective user: order-on-behalf changes who the timeline is opened for.
type StoredSelectionByUser = Record<string, ResourceFilterElementValue[]>

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function isKeyOrValue(value: unknown): value is string | number {
    return typeof value === "string" || typeof value === "number"
}

function isFilterValue(value: unknown): value is ResourceFilterElementValue {
    if (typeof value !== "object" || value === null) return false

    return "key" in value && "value" in value && isKeyOrValue(value.key) && isKeyOrValue(value.value)
}

function readStoredSelections(): StoredSelectionByUser {
    try {
        const raw = LocalCache().get(StorageKey.CREATE_ORDER_TIMELINE_LOCATIONS)
        if (!raw) return {}

        const parsed: unknown = JSON.parse(raw)
        if (!isRecord(parsed)) return {}

        return Object.entries(parsed).reduce<StoredSelectionByUser>((acc, [userId, values]) => {
            const filterValues = Array.isArray(values) ? values.filter(isFilterValue) : []
            if (filterValues.length) acc[userId] = filterValues
            return acc
        }, {})
    } catch {
        // Unreadable storage (truncated entry, private mode) reads as "nothing saved".
        return {}
    }
}

function isSameSelection(
    a: ReadonlyArray<ResourceFilterElementValue>,
    b: ReadonlyArray<ResourceFilterElementValue>,
): boolean {
    return (
        a.length === b.length && a.every((value, index) => value.key === b[index].key && value.value === b[index].value)
    )
}

/** The saved selection as a filter row, ready to spread into the timeline's initial filters. */
export function loadServiceCenterFilter(userId: number): ResourceFiltersSelectedItem[] {
    const values = readStoredSelections()[String(userId)]

    return values?.length ? [{ identifier: SERVICE_CENTER_FILTER_IDENTIFIER, values }] : []
}

export function saveServiceCenterFilter(userId: number, values: ReadonlyArray<ResourceFilterElementValue>): void {
    const stored = readStoredSelections()
    const userKey = String(userId)

    if (isSameSelection(stored[userKey] ?? [], values)) return

    if (values.length) {
        stored[userKey] = values.map((value) => ({ ...value }))
    } else {
        // No entry means "all locations", which is what an empty selection means too.
        delete stored[userKey]
    }

    try {
        LocalCache().set(StorageKey.CREATE_ORDER_TIMELINE_LOCATIONS, JSON.stringify(stored))
    } catch {
        // A full or read-only storage costs the preference, never the booking flow.
    }
}
